import { useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useQuery } from '@tanstack/react-query';
import { useQueues, useSaveQueue, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api } from '../../lib/api/client.js';
import { formatDuration } from '../../lib/format.js';
import type { Queue, Destination } from '@arcnet/contracts';

export function QueuesTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need queue:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('queue:write');
  const queues = useQueues(tenantId);
  const del = useDeleteTms(tenantId, 'queues');
  const [editing, setEditing] = useState<Queue | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Queues</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Hold callers until an agent is free, with position announcements.
          </p>
        </div>
        <div className="arc-spacer" />
        {mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New queue'}
        </button>
        )}
      </div>


      {queues.error && <ErrorBanner error={queues.error} />}
      {(creating || editing) && (
        <QueueEditor tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Strategy</th>
                <th className="arc-td-num">Agents</th>
                <th className="arc-td-num">Max wait</th>
                <th>On timeout</th><th />
              </tr>
            </thead>
            {queues.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {queues.data?.data.map((q) => (
                  <tr key={q.id} className="arc-row-editable" onDoubleClick={() => { setEditing(q); setCreating(false); }}>
                    <td style={{ fontWeight: 550 }}>{q.name}</td>
                    <td className="arc-mono">{q.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td><Badge tone="muted">{q.strategy.replace(/_/g, ' ')}</Badge></td>
                    <td className="arc-td-num">{q.agents.length}</td>
                    <td className="arc-td-num">{formatDuration(q.max_wait_sec)}</td>
                    <td><DestinationLabel tenantId={tenantId} destination={q.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <RowAction kind="edit" label={`Edit ${q.name}`}
                          onClick={() => { setEditing(q); setCreating(false); }} />
                        )}
                        {mayWrite && (
                        <RowAction kind="delete" label={`Delete ${q.name}`}
                          onClick={() => { if (confirm(`Delete queue '${q.name}'?`)) del.mutate(q.id); }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {queues.data !== undefined && queues.data?.data.length === 0 && (
          <Empty icon="queue" title="No queues"
            hint="A queue holds callers with music and announcements until an agent is free." />
        )}
      </div>
    </>
  );
}

function QueueEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: Queue | null; onDone: () => void;
}) {
  const save = useSaveQueue(tenantId);
  const extensions = useExtensions(tenantId, { limit: 200 });

  const [name, setName] = useState(existing?.name ?? '');
  const [ext, setExt] = useState(existing?.extension ?? '');
  const [strategy, setStrategy] = useState(existing?.strategy ?? 'longest_idle');
  const [maxWait, setMaxWait] = useState(String(existing?.max_wait_sec ?? 600));
  const [wrapup, setWrapup] = useState(String(existing?.wrapup_sec ?? 10));
  const [mustMark, setMustMark] = useState(existing?.require_disposition ?? false);
  // Off unless the queue asks for it (0117): 30 days is what it starts at when switched on.
  const [sticky, setSticky] = useState(existing?.sticky_agent_days != null);
  const [stickyDays, setStickyDays] = useState(String(existing?.sticky_agent_days ?? 30));
  const [stickyRing, setStickyRing] = useState(String(existing?.sticky_ring_sec ?? 15));
  const [announce, setAnnounce] = useState(existing?.announce_position ?? true);
  const [timeout_, setTimeout_] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [agents, setAgents] = useState<string[]>(existing?.agents.map((a) => a.extension_id) ?? []);
  const [moh, setMoh] = useState<string>(existing?.moh_media_id ?? '');

  // The same query the Media screens use, so the list is shared.
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{
      data: Array<{ id: string; kind: string; name: string }>; moh_default_id?: string | null;
    }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const mohFiles = (media.data?.data ?? []).filter((m) => m.kind === 'moh');
  const accountMoh = mohFiles.find((m) => m.id === media.data?.moh_default_id)?.name;

  const toggle = (id: string) =>
    setAgents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name, extension: ext || null, strategy,
          max_wait_sec: Number(maxWait), wrapup_sec: Number(wrapup),
          require_disposition: mustMark,
          sticky_agent_days: sticky ? Number(stickyDays) : null,
          sticky_ring_sec: Number(stickyRing),
          announce_position: announce,
          timeout_destination: timeout_,
          moh_media_id: moh || null,
          agents: agents.map((id, i) => ({ extension_id: id, tier_level: 1, tier_position: i + 1 })),
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New queue'}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 100px 1fr 110px 110px', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="q-name">Name</label>
          <input id="q-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Support" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="q-ext">Dial code</label>
          <input id="q-ext" className="arc-input" value={ext}
            onChange={(e) => setExt(e.target.value)} placeholder="300" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="q-strat">Agent selection</label>
          <select id="q-strat" className="arc-select" value={strategy}
            onChange={(e) => setStrategy(e.target.value as typeof strategy)}>
            <option value="longest_idle">Longest idle</option>
            <option value="round_robin">Round robin</option>
            <option value="ring_all">Ring all</option>
            <option value="top_down">Top down</option>
            <option value="agent_with_least_talk_time">Least talk time</option>
            <option value="random">Random</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="q-wait">Max wait (s)</label>
          <input id="q-wait" className="arc-input" inputMode="numeric" value={maxWait}
            onChange={(e) => setMaxWait(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="q-wrap">Wrap-up (s)</label>
          <input id="q-wrap" className="arc-input" inputMode="numeric" value={wrapup}
            onChange={(e) => setWrapup(e.target.value)} />
          <div className="arc-hint">After a call from this queue, before another is offered.</div>
        </div>
      </div>

      {/* What this queue's calls need saying about them (0111). Beside the wrap-up time,
          because they are the same decision about the same moment. */}
      <div className="arc-row" style={{ gap: 10, marginTop: 4 }}>
        <Switch id="q-must-mark" checked={mustMark} onChange={setMustMark} />
        <label htmlFor="q-must-mark" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Agents must mark a call from this queue before going ready
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            They can still take a break while it is unmarked. An agent given their own answer on
            their extension overrides this.
          </span>
        </label>
      </div>

      {/*
        Sticky agents (0117, owner 25 September 2026). Under the queue's timing rather than with
        the agents, because it is a decision about how a call is routed, not about who is in it.
      */}
      <div className="arc-row" style={{ gap: 10, marginTop: 10 }}>
        <Switch id="q-sticky" checked={sticky} onChange={setSticky} />
        <label htmlFor="q-sticky" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Send a caller back to the agent who last dealt with them
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            Calls the agent placed count as well as calls they answered. If they are busy, away or
            do not answer, the call goes into the queue as usual.
          </span>
        </label>
      </div>

      {sticky && (
        <div className="arc-row" style={{ gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          <div className="arc-field" style={{ margin: 0, maxWidth: 150 }}>
            <label className="arc-label" htmlFor="q-sticky-days">Remember for (days)</label>
            <input id="q-sticky-days" className="arc-input arc-num" inputMode="numeric" value={stickyDays}
              onChange={(e) => setStickyDays(e.target.value.replace(/[^0-9]/g, ''))} />
            <div className="arc-hint">Refreshed by every answered call, so an active customer stays.</div>
          </div>
          <div className="arc-field" style={{ margin: 0, maxWidth: 150 }}>
            <label className="arc-label" htmlFor="q-sticky-ring">Ring them for (s)</label>
            <input id="q-sticky-ring" className="arc-input arc-num" inputMode="numeric" value={stickyRing}
              onChange={(e) => setStickyRing(e.target.value.replace(/[^0-9]/g, ''))} />
            <div className="arc-hint">Then the queue. 5 to 60.</div>
          </div>
        </div>
      )}

      <div className="arc-field">
        <label className="arc-label">Agents</label>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 5,
          border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r)',
          padding: 9, maxHeight: 150, overflowY: 'auto',
        }}>
          {(extensions.data?.data ?? []).filter((e) => e.may_queue !== false).map((e) => {
            const on = agents.includes(e.id);
            return (
              <button key={e.id} type="button"
                className={`arc-badge ${on ? 'arc-badge-info' : 'arc-badge-muted'}`}
                style={{ cursor: 'pointer', border: on ? undefined : '1px dashed var(--arc-border-strong)' }}
                onClick={() => toggle(e.id)} aria-pressed={on}>
                {on && <Icon name="check" size={10} />}
                {e.number} {e.display_name}
              </button>
            );
          })}
        </div>
        <div className="arc-hint">
          {agents.length} selected
          {(() => {
            // Said once, rather than listing seats that cannot be chosen (owner, 16 September 2026).
            const gated = (extensions.data?.data ?? []).filter((e) => e.may_queue === false).length;
            return gated > 0
              ? ` · ${gated} extension${gated === 1 ? '' : 's'} not shown: queue membership is not in their type`
              : '';
          })()}
        </div>
      </div>

      <div className="arc-row" style={{ marginBottom: 12, gap: 10 }}>
        <Switch id="q-ann" checked={announce} onChange={setAnnounce} />
        <label htmlFor="q-ann" style={{ fontSize: 12.5, cursor: 'pointer' }}>Announce position in queue</label>
      </div>

      <div className="arc-field" style={{ maxWidth: 420 }}>
        <label className="arc-label" htmlFor="q-moh">Music while waiting</label>
        <select id="q-moh" className="arc-select" value={moh} onChange={(e) => setMoh(e.target.value)}>
          <option value="">{accountMoh ? `Account hold music (${accountMoh})` : 'Account hold music (platform default)'}</option>
          {mohFiles.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div className="arc-field">
        <label className="arc-label">If the wait is exceeded</label>
        <DestinationPicker tenantId={tenantId} value={timeout_} onChange={setTimeout_} allowNone />
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create queue'}
      </button>
    </form>
  );
}
